// src/services/sheets/SheetActions.ts
// Action type definitions for the Sheets AI Copilot

import type { ColumnSemanticType, CellFormat } from "@/features/sheets";
import type { SheetGenerationSpec } from "./SheetsAIService";

// ─────────────────────────────────────────────────────────────────────────────
// Action Types
// ─────────────────────────────────────────────────────────────────────────────

export type SheetActionType =
  | "CREATE_SHEET"
  | "ANALYZE_SHEET"
  | "EDIT_CELLS"
  | "ADD_COLUMNS"
  | "ADD_ROWS"
  | "DELETE_ROWS"
  | "DELETE_COLUMNS"
  | "TEXT_RESPONSE";

// ─────────────────────────────────────────────────────────────────────────────
// Individual Action Payloads
// ─────────────────────────────────────────────────────────────────────────────

/** Create a new sheet from a specification */
export interface CreateSheetAction {
  type: "CREATE_SHEET";
  spec: SheetGenerationSpec;
  description: string;
}

/** Analysis and explanation results for an existing sheet */
export interface AnalyzeSheetAction {
  type: "ANALYZE_SHEET";
  analysis: {
    summary: string;
    insights: string[];
    columnExplanations?: Array<{
      column: string;
      meaning: string;
      interpretation?: string;
    }>;
    keyMetrics?: Array<{
      name: string;
      value: string;
      interpretation: string;
    }>;
    dataQuality: "good" | "fair" | "needs_attention";
    recommendations?: string[];
  };
}

/** Edit specific cells in an existing sheet */
export interface EditCellsAction {
  type: "EDIT_CELLS";
  edits: Array<{
    row: number;
    col: number;
    oldValue: string;
    newValue: string;
    reason: string;
  }>;
  summary: string;
}

/** Add new columns to an existing sheet */
export interface AddColumnsAction {
  type: "ADD_COLUMNS";
  columns: Array<{
    afterCol: number;           // Insert after this column index (-1 for beginning)
    header: string;
    semanticType: ColumnSemanticType;
    formula?: string;           // Formula pattern if applicable (with {row} placeholder)
    liveMetric?: string;        // LIVE() metric if applicable
    format?: Partial<CellFormat>;
    reason: string;
  }>;
  summary: string;
}

/** Add new rows to an existing sheet */
export interface AddRowsAction {
  type: "ADD_ROWS";
  rows: Array<{
    afterRow: number;           // Insert after this row index (-1 for beginning)
    values: (string | null)[];  // null = derive from column formula/live
    ticker?: string;            // If this is a ticker-based row
    reason: string;
  }>;
  summary: string;
}

/** Delete rows from an existing sheet */
export interface DeleteRowsAction {
  type: "DELETE_ROWS";
  rowIndices: number[];
  reason: string;
}

/** Delete columns from an existing sheet */
export interface DeleteColumnsAction {
  type: "DELETE_COLUMNS";
  colIndices: number[];
  reason: string;
}

/** Conversational text response (no sheet modifications) */
export interface TextResponseAction {
  type: "TEXT_RESPONSE";
  content: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Union Type for All Actions
// ─────────────────────────────────────────────────────────────────────────────

export type SheetAction =
  | CreateSheetAction
  | AnalyzeSheetAction
  | EditCellsAction
  | AddColumnsAction
  | AddRowsAction
  | DeleteRowsAction
  | DeleteColumnsAction
  | TextResponseAction;

// ─────────────────────────────────────────────────────────────────────────────
// AI Response Container
// ─────────────────────────────────────────────────────────────────────────────

/** Complete response from the Sheets AI */
export interface SheetAIResponse {
  /** List of actions the AI wants to perform */
  actions: SheetAction[];
  /** Natural language explanation for the user */
  conversationalText?: string;
  /** Whether to show preview and require user confirmation */
  requiresConfirmation: boolean;
  /** AI's confidence in the response */
  confidence: "high" | "medium" | "low";
}

// ─────────────────────────────────────────────────────────────────────────────
// Type Guards
// ─────────────────────────────────────────────────────────────────────────────

export function isCreateSheetAction(action: SheetAction): action is CreateSheetAction {
  return action.type === "CREATE_SHEET";
}

export function isAnalyzeSheetAction(action: SheetAction): action is AnalyzeSheetAction {
  return action.type === "ANALYZE_SHEET";
}

export function isEditCellsAction(action: SheetAction): action is EditCellsAction {
  return action.type === "EDIT_CELLS";
}

export function isAddColumnsAction(action: SheetAction): action is AddColumnsAction {
  return action.type === "ADD_COLUMNS";
}

export function isAddRowsAction(action: SheetAction): action is AddRowsAction {
  return action.type === "ADD_ROWS";
}

export function isDeleteRowsAction(action: SheetAction): action is DeleteRowsAction {
  return action.type === "DELETE_ROWS";
}

export function isDeleteColumnsAction(action: SheetAction): action is DeleteColumnsAction {
  return action.type === "DELETE_COLUMNS";
}

export function isTextResponseAction(action: SheetAction): action is TextResponseAction {
  return action.type === "TEXT_RESPONSE";
}

/** Check if an action modifies the sheet (requires confirmation) */
export function isModifyingAction(action: SheetAction): boolean {
  return [
    "CREATE_SHEET",
    "EDIT_CELLS",
    "ADD_COLUMNS",
    "ADD_ROWS",
    "DELETE_ROWS",
    "DELETE_COLUMNS",
  ].includes(action.type);
}

/** Check if an action is read-only (analysis/explanation) */
export function isReadOnlyAction(action: SheetAction): boolean {
  return [
    "ANALYZE_SHEET",
    "TEXT_RESPONSE",
  ].includes(action.type);
}

// ─────────────────────────────────────────────────────────────────────────────
// Action Validation
// ─────────────────────────────────────────────────────────────────────────────

const VALID_ACTION_TYPES: SheetActionType[] = [
  "CREATE_SHEET",
  "ANALYZE_SHEET",
  "EDIT_CELLS",
  "ADD_COLUMNS",
  "ADD_ROWS",
  "DELETE_ROWS",
  "DELETE_COLUMNS",
  "TEXT_RESPONSE",
];

/** Validate that an action has the correct structure */
export function validateAction(action: unknown): action is SheetAction {
  if (!action || typeof action !== "object") return false;
  const actionObj = action as { type?: unknown };
  if (!actionObj.type || typeof actionObj.type !== "string") return false;
  return VALID_ACTION_TYPES.includes(actionObj.type as SheetActionType);
}

/** Get a human-readable summary of an action */
export function getActionSummary(action: SheetAction): string {
  switch (action.type) {
    case "CREATE_SHEET":
      return `Create sheet: ${action.spec.name}`;
    case "ANALYZE_SHEET":
      return action.analysis.summary;
    case "EDIT_CELLS":
      return `Edit ${action.edits.length} cell(s): ${action.summary}`;
    case "ADD_COLUMNS":
      return `Add ${action.columns.length} column(s): ${action.summary}`;
    case "ADD_ROWS":
      return `Add ${action.rows.length} row(s): ${action.summary}`;
    case "DELETE_ROWS":
      return `Delete ${action.rowIndices.length} row(s): ${action.reason}`;
    case "DELETE_COLUMNS":
      return `Delete ${action.colIndices.length} column(s): ${action.reason}`;
    case "TEXT_RESPONSE":
      return action.content.slice(0, 100) + (action.content.length > 100 ? "..." : "");
    default:
      return "Unknown action";
  }
}
